import React, { useState, useEffect, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { Grid } from '@mui/material';
import Search from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';
import OfferAssetInformationComponent from './OfferAssetInformationComponent';
import AddIcon from '@mui/icons-material/Add';
import * as editableDocument from 'utils/model/editableDocument';
import { collection } from 'utils/immutable';
import { isMandatory, isChassisNumber } from 'utils/simple-validations';
import flow from 'lodash.flow';
import { useDispatch } from 'react-redux';
import { saveAssetInformation, deleteAssetInformation } from 'features/offer/edit/assets/asset/actionCreators';
import { Card, IconButton, TextField } from '@totalsoft/rocket-ui';
const getValidationRules = () => {
  return {
    chassisNumber: [isMandatory(), isChassisNumber()]
  };
};

export const OfferAssetInformationsContainer = ({
  t,
  leasingDocumentAssetsInformations,
  documentId,
  siteId,
  documentDetailId,
  editRight
}) => {
  const [assetInformations, setAssetInformations] = useState([]);
  const [filteredAssetInformations, setFilteredAssetInformations] = useState([]);
  const [textFilter, setTextFilter] = useState('');
  const [disabled, setDisabled] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (leasingDocumentAssetsInformations) {
      setAssetInformations(leasingDocumentAssetsInformations.map(editableDocument.create));
    }
  }, [leasingDocumentAssetsInformations]);

  const filterAssetInformations = useCallback(
    filter => {
      if (filter === '') return assetInformations;
      var filteredAssetInformations = assetInformations.filter(x => {
        return x.document.chassisNumber?.includes(filter) || x.document.orderNumber?.toString().includes(filter);
      });
      return filteredAssetInformations;
    },
    [assetInformations]
  );

  useEffect(() => {
    if (assetInformations) {
      setFilteredAssetInformations(filterAssetInformations(textFilter));
    }
  }, [assetInformations, filterAssetInformations, textFilter]);

  const onFilterChange = useCallback(
    textFilter => {
      var filteredAssetInformations = filterAssetInformations(textFilter);
      setTextFilter(textFilter);
      setFilteredAssetInformations(filteredAssetInformations);
    },
    [filterAssetInformations]
  );

  const onPropChange = useCallback(
    (assetInformation, prop, value) => {
      const idx = assetInformations.findIndex(x => x.document.orderNo === assetInformation.orderNo);
      setAssetInformations(
        collection.update(
          idx,
          flow(editableDocument.update(prop, value, null), editableDocument.validate(getValidationRules(), null)),
          assetInformations
        )
      );
      var filteredAssetInformations = filterAssetInformations(textFilter);
      setFilteredAssetInformations(filteredAssetInformations);
    },
    [assetInformations, filterAssetInformations, textFilter]
  );

  const addAssetInformation = useCallback(() => {
    setDisabled(true);
    const orderNo =
      Math.max.apply(
        Math,
        assetInformations.map(x => x.document.orderNo)
      ) + 1;

    const newAssetInformationList = collection.insert(
      assetInformations.length,
      editableDocument.create({ id: null, orderNo, orderNumber: null, chassisNumber: null }),
      assetInformations
    );

    setAssetInformations(newAssetInformationList);
    const { document: newAssetInformation } = newAssetInformationList[newAssetInformationList.length - 1];

    dispatch(saveAssetInformation(documentId, siteId, documentDetailId, newAssetInformation, editRight)).then(() => setDisabled(false));
  }, [assetInformations, dispatch, documentDetailId, documentId, editRight, siteId]);

  const saveAssetInformationHandler = useCallback(
    assetInformation => {
      setDisabled(true);
      const idx = assetInformations.findIndex(x => x.document.orderNo === assetInformation.orderNo);
      setAssetInformations(collection.update(idx, editableDocument.validate(getValidationRules(), null), assetInformations));

      if (!assetInformations[idx].validationInfo.isValid) return;

      dispatch(saveAssetInformation(documentId, siteId, documentDetailId, assetInformation, editRight)).then(() => setDisabled(false));
    },
    [assetInformations, dispatch, documentDetailId, documentId, editRight, siteId]
  );

  const deleteAssetInformationHandler = useCallback(
    (assetInformation, idx) => {
      if (assetInformation.id) {
        setDisabled(true);
        dispatch(deleteAssetInformation(documentId, siteId, documentDetailId, assetInformation, editRight)).then(() => setDisabled(false));
      } else {
        setAssetInformations(collection.remove(idx, assetInformations));
      }
    },
    [assetInformations, dispatch, documentDetailId, documentId, editRight, siteId]
  );

  return (
    <Grid container>
      <Grid item xs={12}>
        <Grid container spacing={2} alignItems='center'>
          <Grid item flexGrow={1}>
            <TextField
              id={'assetInformation_filters_fullText'}
              fullWidth
              name='fullTextFilter'
              value={textFilter}
              disabled={disabled}
              onChange={value => {
                onFilterChange(value);
              }}
              placeholder={t('OfferEdit.AssetInformations.Search')}
              startAdornment={<Search />}
              endAdornment={
                <IconButton
                  onClick={() => {
                    onFilterChange('');
                  }}
                  tooltip={t('Tooltips.ResetFilters')}
                  id={'assetInformation_filters_resetBtn'}
                  variant='text'
                  color='primary'
                  size='tiny'
                  disabled={disabled}
                >
                  <Close />
                </IconButton>
              }
            />
          </Grid>
          <Grid item>
            <IconButton
              id={'assetInformation_addBtn'}
              color='primary'
              variant='text'
              tooltip={t('Add')}
              onClick={addAssetInformation}
              disabled={disabled}
            >
              <AddIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={1} mt={2}>
        {filteredAssetInformations &&
          filteredAssetInformations.map((item, idx) => {
            return (
              <Grid item xs={12} key={idx}>
                <Card disablePadding sx={{ p: 2 }}>
                  <OfferAssetInformationComponent
                    disabled={disabled}
                    saveAssetInformation={saveAssetInformationHandler}
                    validationInfo={item.validationInfo}
                    onPropertyChange={onPropChange}
                    idx={idx}
                    dirtyInfo={item.dirtyInfo}
                    item={item.document}
                    deleteAssetInformation={deleteAssetInformationHandler}
                  />
                </Card>
              </Grid>
            );
          })}
      </Grid>
    </Grid>
  );
};

OfferAssetInformationsContainer.propTypes = {
  t: PropTypes.func.isRequired,
  leasingDocumentAssetsInformations: PropTypes.array.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  documentDetailId: PropTypes.number.isRequired,
  editRight: PropTypes.bool.isRequired
};

export default compose(withTranslation())(memo(OfferAssetInformationsContainer));
